import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Header } from 'components';
import { PrimaryButton } from 'utils';
import brandRoles from 'utils/brandRoles';
import { projectsServices } from 'services';
import {
  FONT,
  Section,
} from 'scenes/YourProjectDetails/components/ui';

/**
 * Abmelden von Update-Mails eines einzelnen Projekts (Link aus der Update-Mail).
 * Das Abmelden passiert erst hier per JavaScript, nicht beim Aufruf des Links:
 * Mailprogramme rufen Links teils vorab auf, um sie zu pruefen.
 * Der Newsletter und das Folgen/Speichern des Projekts bleiben unberuehrt.
 */
function UpdateUnsubscribePage({ location }) {
  const token = new URLSearchParams(location.search).get('token') || '';
  const [state, setState] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | invalid | error
  const [busy, setBusy] = useState(false);

  const request = (method) =>
    projectsServices.updateUnsubscribeRequest(method, token).then((res) => {
      setState(res);
      setPhase('ready');
    });

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    request('POST').catch(() => {
      // 404 = ungueltiger Link, alles andere = voruebergehender Fehler
      projectsServices
        .updateUnsubscribeRequest('GET', token)
        .then(() => setPhase('error'))
        .catch(() => setPhase('invalid'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggle = () => {
    setBusy(true);
    request(state.unsubscribed ? 'DELETE' : 'POST')
      .catch(() => setPhase('error'))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Header />
      <Main>
        <Section>
          {phase === 'loading' && <Text>Einen Moment …</Text>}

          {phase === 'invalid' && (
            <>
              <Title>Dieser Link funktioniert nicht</Title>
              <Text>
                Er ist unvollständig oder ungültig. Öffne ihn am besten direkt
                aus der E-Mail.
              </Text>
            </>
          )}

          {phase === 'error' && (
            <>
              <Title>Das hat nicht geklappt</Title>
              <Text>Versuch es in ein paar Minuten noch einmal.</Text>
            </>
          )}

          {phase === 'ready' && state && (
            <>
              <Title>
                {state.unsubscribed
                  ? `Du bekommst keine Updates mehr zu „${state.project_title}“`
                  : `Du bekommst wieder Updates zu „${state.project_title}“`}
              </Title>
              <Text>
                {state.unsubscribed
                  ? 'Andere Projekte und unser Newsletter sind davon nicht betroffen. Wenn du das Projekt gespeichert hast, bleibt es in deinen gespeicherten Projekten.'
                  : `Neue Updates kommen wieder per E-Mail an ${state.email}.`}
              </Text>
              {state.unsubscribed_all && (
                <Text>
                  Über den Abmelde-Knopf deines E-Mail-Programms hast du
                  außerdem alle Projekt-Updates abbestellt. Wenn du sie wieder
                  bekommen möchtest, <Link to="/contact-us">schreib uns</Link>.
                </Text>
              )}
              <Actions>
                <PrimaryButton
                  primary={true}
                  type="button"
                  onClick={toggle}
                  disabled={busy}
                >
                  {state.unsubscribed ? 'Rückgängig machen' : 'Doch abmelden'}
                </PrimaryButton>
                <Link className="project-link" to={`/project/${state.project_url}`}>
                  Zum Projekt
                </Link>
              </Actions>
            </>
          )}
        </Section>
      </Main>
    </>
  );
}

UpdateUnsubscribePage.propTypes = {
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
};

export default UpdateUnsubscribePage;

const Main = styled.main`
  max-width: 560px;
  margin: 0 auto;
  padding: 64px 16px;
  font-family: ${FONT};
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${FONT};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  color: ${brandRoles.neutral800};
`;

const Text = styled.p`
  margin: 12px 0 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${brandRoles.neutral700};

  a {
    color: ${brandRoles.linkText};
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  margin-top: 24px;

  button {
    margin-bottom: 0;
  }

  .project-link {
    font-size: 14px;
    font-weight: 500;
    color: ${brandRoles.linkText};
    text-decoration: underline;

    &:hover {
      color: ${brandRoles.linkHover};
    }
  }
`;
